import React, { createContext, useState, useEffect, useContext } from 'react';
import { authAPI } from '../services/api';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const clearSession = () => {
      localStorage.removeItem('user');
      if (!cancelled) {
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    const applyUser = (updatedUser) => {
      if (!updatedUser) {
        clearSession();
        return;
      }
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (!cancelled) {
        setUser(updatedUser);
        setIsAuthenticated(true);
      }
    };

    const hadCachedUser = Boolean(localStorage.getItem('user'));

    // Check if user is logged in by calling /auth/me.
    // Cookies (HttpOnly) are automatically sent with the request.
    // If access expired, /me may return user:null (soft JWT) while refresh
    // cookie is still valid — retry once via refresh so visiting public pages
    // does not force a full re-login.
    authAPI
      .getCurrentUser()
      .then(async (response) => {
        if (cancelled) return;
        const updatedUser = response.data.user;
        if (updatedUser) {
          applyUser(updatedUser);
          return;
        }
        if (!hadCachedUser) {
          clearSession();
          return;
        }
        try {
          await authAPI.refreshToken();
          const retry = await authAPI.getCurrentUser();
          if (cancelled) return;
          applyUser(retry.data?.user);
        } catch (refreshError) {
          if (refreshError?.silent || refreshError?.isExpected) {
            // Expected when there is truly no refresh cookie
          } else if (refreshError?.response?.status && refreshError.response.status !== 401) {
            logger.error('Error refreshing session on bootstrap:', refreshError);
          }
          clearSession();
        }
      })
      .catch(async (error) => {
        // Not authenticated or token invalid - this is normal if user hasn't logged in
        if (error?.silent || error?.isExpected) {
          // expected
        } else if (error?.response?.status === 401) {
          // 401 on /auth/me is expected when not logged in
        } else if (error?.response?.status && error.response.status !== 401) {
          logger.error('Error checking authentication:', error);
        } else if (!error?.response) {
          logger.debug('Network error checking authentication (user may be offline):', error);
        }

        if (hadCachedUser && error?.response?.status === 401) {
          try {
            await authAPI.refreshToken();
            const retry = await authAPI.getCurrentUser();
            if (cancelled) return;
            applyUser(retry.data?.user);
            return;
          } catch (_) {
            // fall through to clear
          }
        }
        clearSession();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onExpired = () => {
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener('oikra:session-expired', onExpired);
    return () => window.removeEventListener('oikra:session-expired', onExpired);
  }, []);

  const login = async (email, password) => {
    try {
      // Validate inputs
      if (!email || !password) {
        return {
          success: false,
          error: 'Email and password are required.',
        };
      }

      const response = await authAPI.login({ email, password });
      const { user } = response.data;

      // Store user data (not sensitive, safe to store in localStorage)
      localStorage.setItem('user', JSON.stringify(user));
      
      // Tokens are now stored in HttpOnly cookies automatically
      // Do NOT store tokens in localStorage for security (prevents XSS attacks)

      setUser(user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      logger.error('Login error:', error);
      
      // Better error messages based on status code
      let errorMessage = 'Login failed. Please check your credentials and try again.';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          errorMessage = data?.error || 'Invalid email or password. Please check your credentials.';
        } else if (status === 429) {
          errorMessage = 'Too many login attempts. Please wait a moment and try again.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later or contact support.';
        } else if (status >= 400 && status < 500) {
          errorMessage = data?.error || 'Invalid request. Please check your input.';
        } else {
          errorMessage = data?.error || `Server error (${status}). Please try again.`;
        }
      } else if (error.request) {
        errorMessage = 'Cannot connect to server. Please check if the backend is running and try again.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      if (!credential) {
        return { success: false, error: 'No credential from Google.' };
      }
      const response = await authAPI.googleLogin({ credential });
      const nextUser = response.data.user;
      if (!nextUser) {
        return { success: false, error: 'Invalid response from server.' };
      }
      localStorage.setItem('user', JSON.stringify(nextUser));
      setUser(nextUser);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      logger.error('Google login error:', error);
      const status = error.response?.status;
      const msg = error.response?.data?.error;
      if (status === 503) {
        return {
          success: false,
          error: msg || 'El inicio de sesión con Google no está configurado en el servidor.',
        };
      }
      if (status === 403) {
        return { success: false, error: msg || 'Tu cuenta de Google no tiene el email verificado.' };
      }
      if (status === 401) {
        return { success: false, error: msg || 'Credencial de Google no válida.' };
      }
      return {
        success: false,
        error: msg || 'Error al iniciar sesión con Google.',
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { user } = response.data;

      // Store user data (not sensitive, safe to store in localStorage)
      localStorage.setItem('user', JSON.stringify(user));
      
      // Tokens are now stored in HttpOnly cookies automatically
      // Do NOT store tokens in localStorage for security (prevents XSS attacks)

      setUser(user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      logger.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Registration failed. Please check if the backend is running.',
      };
    }
  };

  const acceptInvite = async ({ token, password }) => {
    try {
      const response = await authAPI.acceptInvite({ token, password });
      const { user } = response.data;
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      logger.error('Invite accept error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Invite accept failed.',
      };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear HttpOnly cookies
      await authAPI.logout();
    } catch (error) {
      // Even if logout fails, clear local state
      logger.error('Logout error:', error);
    } finally {
      // Clear local storage (user data)
      // HttpOnly cookies are cleared by the backend logout endpoint
      localStorage.removeItem('user');
      // Note: We don't need to clear tokens from localStorage anymore
      // as they are stored in HttpOnly cookies
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      const next = response.data.user;
      if (next) {
        setUser(next);
        localStorage.setItem('user', JSON.stringify(next));
        setIsAuthenticated(true);
      }
    } catch (error) {
      logger.error('refreshUser failed:', error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithGoogle,
    register,
    acceptInvite,
    logout,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
